import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../App';
import { Bell, BellRing } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const NotificationBell = ({ user }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, right: 0 });
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const lastSeenIdRef = useRef(null); // Iter380: track last latest notification id to detect NEW arrivals

  const fetchNotifications = useCallback(async () => {
    try {
      // Lightweight count poll (Iter380)
      const countRes = await axios.get(`${API}/notifications/unread-count`);
      const newCount = countRes.data.unread_count || 0;
      const latest = countRes.data.latest;

      // Detect a freshly arrived unread notification (id changed since last poll)
      if (latest && latest.notification_id && lastSeenIdRef.current && latest.notification_id !== lastSeenIdRef.current && newCount > unreadCount) {
        toast(latest.title, {
          description: latest.message?.slice(0, 120),
          action: latest.action_url ? {
            label: 'Open',
            onClick: () => { window.location.href = latest.action_url; },
          } : undefined,
          duration: 6000,
        });
      }
      if (latest?.notification_id) {
        lastSeenIdRef.current = latest.notification_id;
      }
      setUnreadCount(newCount);
    } catch (error) {
      // silent — bell is decorative
    }
  }, [unreadCount]);

  // Heavier full-list fetch only when panel is opened
  const fetchPanelList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications?limit=5`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  // Calculate panel position when opened
  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [open]);

  const fetchNotificationsLegacy = async () => {
    // Iter380: Replaced by fetchPanelList — kept here for any external caller.
    return fetchPanelList();
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      fetchPanelList();
      fetchNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      fetchPanelList();
      fetchNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60); // difference in minutes

    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff} min ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)} hours ago`;
    return `${Math.floor(diff / 1440)} days ago`;
  };

  if (!user) return null;

  const panel = open ? createPortal(
    <div 
      ref={panelRef}
      style={{ top: panelPos.top, right: panelPos.right }}
      className="fixed w-80 rounded-md border bg-popover text-popover-foreground shadow-md z-[200] animate-in fade-in-0 zoom-in-95 slide-in-from-top-2"
      data-testid="notification-panel"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <span className="font-semibold text-sm">Notifications</span>
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-auto p-1"
            onClick={markAllAsRead}
          >
            Mark all as read
          </Button>
        )}
      </div>
      
      {notifications.length === 0 ? (
        <div className="px-3 py-6 text-center text-muted-foreground">
          <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No notifications yet</p>
        </div>
      ) : (
        <div className="max-h-[320px] overflow-y-auto">
          {notifications.map((notification) => (
            <Link
              key={notification.notification_id}
              to={notification.action_url || '/notifications'}
              className={`flex flex-col gap-1 px-3 py-2 hover:bg-accent cursor-pointer transition-colors ${!notification.is_read ? 'bg-blue-50' : ''}`}
              onClick={() => {
                if (!notification.is_read) markAsRead(notification.notification_id);
                setOpen(false);
              }}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{notification.title}</span>
                {!notification.is_read && (
                  <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0"></span>
                )}
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">
                {notification.message}
              </p>
              <span className="text-xs text-muted-foreground">
                {formatTime(notification.created_at)}
              </span>
            </Link>
          ))}
        </div>
      )}

      <div className="border-t">
        <Link 
          to="/notifications" 
          className="block text-center text-sm text-primary py-2 hover:bg-accent transition-colors"
          onClick={() => setOpen(false)}
        >
          View all notifications
        </Link>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <Button 
        ref={triggerRef}
        variant="ghost" 
        size="icon" 
        className="relative"
        onClick={() => {
          setOpen(prev => {
            if (!prev) fetchPanelList(); // Iter380: lazy load full list when opening
            return !prev;
          });
        }}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        data-testid="notification-bell-btn"
      >
        {unreadCount > 0 ? (
          <>
            <BellRing className="h-5 w-5 text-primary animate-pulse" />
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          </>
        ) : (
          <Bell className="h-5 w-5" />
        )}
      </Button>
      {panel}
    </>
  );
};

export default NotificationBell;
